import { useEffect, useState } from "react"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CircularProgress from "@mui/material/CircularProgress"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import VerifiedIcon from "@mui/icons-material/Verified"
import Chip from "@mui/material/Chip"
import { useWallet } from "@solana/wallet-adapter-react"
import type { Ape } from "./useOwnedApes"
import { useDiscovered, useOwnedApes } from "./useOwnedApes"
import Downloads from "./Downloads"
import { NicknameEditor, useNickname } from "./Nickname"
import TransactionHistory from "./TransactionHistory"
import { MINT_URL } from "./MintStatus"
import ApeTraits, { ApeImage, fetchArweaveApe, loadApeIndex, RankChip } from "./ApeTraits"

// Public page for one ape, reached from the QR code on its tag. Works for minted and unminted apes.
const fetchApe = async (dna: string): Promise<Ape> => {
	const index = await loadApeIndex()
	const number = index.findIndex(a => a[0] === dna)
	if (number < 0) throw new Error("This code doesn't match any Bored Ape Pastel.")
	return fetchArweaveApe(number, dna, index[number][1])
}

const ApePage = ({ dna }: { dna: string }) => {
	const [state, setState] = useState<{ ape?: Ape, error?: string }>({})
	const discovered = useDiscovered()
	const { publicKey } = useWallet()
	const { apes: owned } = useOwnedApes()

	useEffect(() => {
		fetchApe(dna).then(ape => {
			document.title = `${ape.name} · Bored Ape Pastel Club`
			setState({ ape })
		}).catch((e: Error) => setState({ error: e.message }))
	}, [dna])

	const { ape, error } = state
	// Hidden until minted; also while the lookup loads or if it fails, so nothing leaks early
	const mint = ape && discovered?.get(ape.number)
	const hidden = !mint
	// Holder perks only for the connected wallet that owns this ape
	const mine = !!ape && !!owned?.some(o => o.number === ape.number)
	// Only minted apes can have a holder, so only they can have a nickname
	const [nickname, setNickname] = useNickname(ape && !hidden ? ape.number : undefined)
	return (
		<>
			<Button href="/" startIcon={<ArrowBackIcon />} sx={{ mb: 3 }}>All apes</Button>
			{error && <Alert severity="warning">{error}</Alert>}
			{!ape && !error && <CircularProgress sx={{ display: "block", mx: "auto", my: 10 }} />}
			{ape && (
				<Grid container spacing={{ xs: 3, md: 6 }} sx={{ alignItems: "center" }}>
					<Grid size={{ xs: 12, md: 5 }}>
						<Card>
							<ApeImage ape={ape} hidden={hidden} />
						</Card>
					</Grid>
					<Grid size={{ xs: 12, md: 7 }}>
						<Typography variant="overline" color="text.secondary">Bored Ape Pastel Club</Typography>
						<Typography variant="h3" component="h1" sx={{ fontSize: { xs: "2rem", md: "2.75rem" } }}>{ape.name}</Typography>
						{nickname && <Typography variant="h5" component="p" sx={{ color: "secondary.dark", mt: .5 }}>“{nickname}”</Typography>}
						<Box sx={{ my: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
							<RankChip number={ape.number} />
							{mine && <Chip size="small" color="primary" icon={<VerifiedIcon />} label="Holder · this ape is yours" sx={{ fontWeight: 600 }} />}
						</Box>
						{hidden
							? <>
								<Typography color="text.secondary">This ape hasn't been minted yet. Its look and traits are revealed once someone mints it.</Typography>
								<Button href={MINT_URL} target="_blank" rel="noopener" variant="contained" color="secondary" sx={{ mt: 2.5, borderRadius: 999, px: 4 }}>Mint now</Button>
							</>
							: <>
								<ApeTraits attributes={ape.attributes} />
								{mine && <Box sx={{ mt: 3 }}><NicknameEditor number={ape.number} nickname={nickname} onSaved={setNickname} /></Box>}
								{mine && <Box sx={{ mt: 3 }}><Downloads ape={ape} dna={dna} /></Box>}
								{mint && <TransactionHistory mint={mint} />}
								{!publicKey && <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>Own this ape? Connect your wallet to name it and get holder downloads.</Typography>}
							</>}
					</Grid>
				</Grid>
			)}
		</>
	)
}

export default ApePage
